/**
 * AS CONTAS DO ROMANEIO — num lugar só, com teste em testes/contas.mjs.
 *
 * A regra de ouro: a conta que o comprador refaz à mão no curral tem de dar
 * igual à do relatório, ao centavo. Por isso:
 *
 *   arrobas      = peso vivo × rendimento ÷ 100 ÷ 15
 *   valor do lote = arrobas × preço   |   kg × preço   |   cabeças × preço
 *   bruto         = soma dos lotes
 *   descontos     = (financeiro% + funrural%) SOBRE O BRUTO, somados —
 *                   «2% de financeiro e 1,63% de funrural, desconta 3,63% do
 *                   bruto». Não em cascata: em cascata o número do curral e o
 *                   do papel não batem.
 *   líquido       = bruto − financeiro − funrural
 *
 * 3D · DESCONTOS POR LOTE (test drive de 06/10/2026: «vacas a 2% + 1,63% e
 * bezerros só com o funrural»). Cada lote tem os seus; lote sem os seus (ficha
 * antiga) usa os da ficha. Continuam somados e tirados do bruto — do bruto DO
 * LOTE:
 *
 *   todos os lotes com os mesmos descontos → a conta de sempre, sobre o bruto
 *     da ficha (um arredondamento só). Assim ficha antiga não muda nem um
 *     centavo: o «custo fechado» e a Catira continuam batendo;
 *   lotes com descontos diferentes → por lote: financeiro do lote = bruto do
 *     lote × financeiro% do lote, ao centavo (idem funrural); o financeiro da
 *     ficha é a soma dos lotes. É a conta que se refaz à mão, lote por lote.
 *
 * Cada valor em reais é arredondado ao centavo na hora em que nasce, como no
 * papel: o valor do lote, o financeiro, o funrural.
 *
 * R1C · A ARROBA DO PAPEL TEM DE BATER COM O VALOR COBRADO (revisão de
 * 06/10/2026). O papel mostra a arroba em 2 casas (33,68 @) e quem refaz à
 * mão multiplica esse número: 33,68 × 320 = 10.777,60. A conta antiga usava a
 * arroba inteira (33,6833… × 320 = 10.778,67) — um real de diferença que o
 * vendedor acha. Desde a R1C, ficha NOVA (`arrobaEmDuasCasas`) arredonda a
 * arroba de cada lote em 2 casas antes de multiplicar. Ficha antiga continua
 * com a conta de sempre: nem um centavo muda (o custo fechado e o estoque
 * continuam batendo).
 *
 * R1C · QUEBRA (%) por lote, combinada com o vendedor: sai do peso da balança
 * antes da arroba (e do kg, no lote por kg). Peso que vale = peso da balança ×
 * (1 − quebra ÷ 100), em 1 casa, como sai no papel. Sem quebra, nada muda.
 */

import type { Comissao, Custo, Ficha, Forma, Lote, Negociacao, Quitacao } from './tipos'

export const KG_POR_ARROBA = 15

export function arrobas(kg: number, rendimento: number): number {
  return (kg * rendimento) / 100 / KG_POR_ARROBA
}

export function centavos(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100
}

/** Arroba em 2 casas, como sai no papel (R1C) — o mesmo arredondamento do centavo. */
export const arrobaDoPapel = centavos

/** O que a conta precisa saber da ficha além de lotes e pesos: a regra da arroba (R1C). */
export type RegraDaArroba = Partial<Pick<Ficha, 'arrobaEmDuasCasas'>>

/** A quebra do lote em %, só quando é um número positivo (lote antigo: 0). */
export function quebraDoLote(l: Pick<Lote, 'quebraPct'>): number {
  return typeof l.quebraPct === 'number' && Number.isFinite(l.quebraPct) && l.quebraPct > 0 ? l.quebraPct : 0
}

/** Peso que vale depois da quebra, em 1 casa (é o número que o papel mostra). Sem quebra: o próprio peso. */
export function pesoQueVale(kg: number, quebraPct: number): number {
  if (!(quebraPct > 0)) return kg
  return Math.round((kg * (1 - quebraPct / 100) + Number.EPSILON) * 10) / 10
}

/**
 * R1C · Rendimento de carcaça aceito: de 40 a 60%. Fora disso é erro de
 * digitação (na revisão, «5252%» virou 1.750 arrobas de um boi de 500 kg).
 */
export const RENDIMENTO_MINIMO = 40
export const RENDIMENTO_MAXIMO = 60

/** A frase do aviso vermelho embaixo do campo, ou null quando o rendimento serve. */
export function problemaDoRendimento(r: number | null): string | null {
  if (r === null) return 'Preencha o rendimento. O normal é de 48 a 56%.'
  if (r < RENDIMENTO_MINIMO || r > RENDIMENTO_MAXIMO) {
    return `Rendimento de ${r.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}% não existe. O normal é de 48 a 56%.`
  }
  return null
}

/**
 * R1C · Rendimento padrão de cada categoria (Ajustes › Padrões). Valores de
 * partida sensatos; cada aparelho muda os seus. Categoria sem padrão usa o
 * rendimento geral dos Ajustes.
 */
export const RENDIMENTOS_PADRAO: Record<string, number> = {
  'Boi': 52, 'Boi gordo': 54, 'Boi magro': 50, 'Touro': 54, 'Tourinho': 52,
  'Vaca': 48, 'Vaca com cria': 48, 'Novilha': 50, 'Novilho': 51, 'Garrote': 50, 'Bezerro': 50, 'Bezerra': 50,
}

/** As categorias que a R1C acrescentou às padrão (decisão do gestor, 06/10/2026). */
export const CATEGORIAS_NOVAS_R1 = ['Novilho', 'Vaca com cria', 'Boi magro', 'Boi gordo', 'Tourinho']

export function rendimentoPadrao(categoria: string, a: { rendimento: number; rendimentos?: Record<string, number> }): number {
  const proprio = a.rendimentos?.[categoria]
  if (typeof proprio === 'number' && !problemaDoRendimento(proprio)) return proprio
  const padrao = RENDIMENTOS_PADRAO[categoria]
  if (padrao !== undefined) return padrao
  return !problemaDoRendimento(a.rendimento) ? a.rendimento : 50
}

/** Os descontos que valem para um lote: os dele ou, se não tem (ficha antiga), os da ficha. */
export interface Descontos { financeiroPct: number; funruralPct: number }

const pct = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null)

export function descontosDoLote(l: Pick<Lote, 'financeiroPct' | 'funruralPct'>, f: Partial<Pick<Ficha, 'financeiroPct' | 'funruralPct'>>): Descontos {
  return {
    financeiroPct: pct(l.financeiroPct) ?? pct(f.financeiroPct) ?? 0,
    funruralPct: pct(l.funruralPct) ?? pct(f.funruralPct) ?? 0,
  }
}

/**
 * Um conjunto de lotes que se desconta junto. Lotes com descontos iguais: um
 * grupo só, com todos (a conta de sempre). Diferentes: um grupo por lote.
 */
export interface GrupoDeDesconto extends Descontos {
  lotes: Lote[]
  bruto: number
  financeiro: number
  funrural: number
}

export interface LinhaDeLote {
  lote: Lote
  /** os descontos que valem para este lote (os dele ou os da ficha) */
  descontos: Descontos
  registros: number
  cab: number
  /** soma dos pesos conhecidos (o peso da BALANÇA) */
  kg: number
  /** R1C · quebra do lote (%) e o peso que vale depois dela (sem quebra, igual a `kg`) */
  quebraPct: number
  kgQueVale: number
  /** cabeças que têm peso — a média só olha para elas */
  cabPesadas: number
  /** algum registro sem peso, num lote que cobra por peso: valor não fecha */
  faltaPeso: boolean
  arrobas: number | null
  valor: number | null
}

export interface Resultado {
  linhas: LinhaDeLote[]
  cab: number
  kg: number
  cabPesadas: number
  media: number | null
  arrobas: number
  temArroba: boolean
  bruto: number
  temPreco: boolean
  /** lotes com animais e sem valor: fora do bruto, e o papel avisa */
  semValor: string[]
  financeiro: number
  funrural: number
  liquido: number
  pendentes: number
  /** 3D · como os descontos foram tirados (ver o topo do arquivo) */
  grupos: GrupoDeDesconto[]
  /** todos os lotes com valor têm os mesmos descontos: o papel mostra o total, como sempre */
  descontosIguais: boolean
  /** os % que valem para a ficha inteira quando são iguais; nulo quando cada lote tem o seu */
  financeiroPct: number | null
  funruralPct: number | null
}

export function calcular(f: Pick<Ficha, 'lotes' | 'registros' | 'financeiroPct' | 'funruralPct'> & RegraDaArroba): Resultado {
  const duasCasas = f.arrobaEmDuasCasas === true
  const linhas: LinhaDeLote[] = f.lotes.map((lote) => {
    const regs = f.registros.filter((r) => r.loteId === lote.id && !r.pendente)
    let cab = 0, peso = 0, cabPesadas = 0, semPeso = 0
    for (const r of regs) {
      cab += r.qtd
      if (r.kg !== null) { peso += r.kg; cabPesadas += r.qtd } else semPeso++
    }
    const faltaPeso = lote.base !== 'cabeca' && semPeso > 0
    // R1C: a quebra sai do peso antes da arroba; ficha nova, arroba em 2 casas (ver o topo)
    const quebra = lote.base === 'cabeca' ? 0 : quebraDoLote(lote)
    const vale = pesoQueVale(peso, quebra)
    const arrInteira = lote.base === 'arroba' ? arrobas(vale, lote.rendimento) : null
    const arr = arrInteira !== null && duasCasas ? arrobaDoPapel(arrInteira) : arrInteira
    let valor: number | null = null
    if (lote.preco !== null && lote.preco > 0 && cab > 0 && !faltaPeso) {
      valor = centavos(lote.base === 'cabeca' ? cab * lote.preco : lote.base === 'kg' ? vale * lote.preco : arr! * lote.preco)
    }
    return { lote, descontos: descontosDoLote(lote, f), registros: regs.length, cab, kg: peso, quebraPct: quebra, kgQueVale: vale, cabPesadas, faltaPeso, arrobas: arr, valor }
  })
  const comAnimais = linhas.filter((l) => l.cab > 0)
  const bruto = centavos(comAnimais.reduce((s, l) => s + (l.valor ?? 0), 0))
  const temPreco = comAnimais.some((l) => l.valor !== null)
  // 3D: só os lotes com dinheiro decidem se os descontos são iguais
  const comValor = comAnimais.filter((l) => l.valor !== null)
  const mesmo = (a: Descontos, b: Descontos) => a.financeiroPct === b.financeiroPct && a.funruralPct === b.funruralPct
  const descontosIguais = comValor.every((l) => mesmo(l.descontos, comValor[0]!.descontos))
  const grupos: GrupoDeDesconto[] = (descontosIguais ? (comValor.length ? [comValor] : []) : comValor.map((l) => [l])).map((ls) => {
    const d = ls[0]!.descontos
    const b = centavos(ls.reduce((s, l) => s + l.valor!, 0))
    return { ...d, lotes: ls.map((l) => l.lote), bruto: b, financeiro: centavos((b * d.financeiroPct) / 100), funrural: centavos((b * d.funruralPct) / 100) }
  })
  const financeiro = centavos(grupos.reduce((s, g) => s + g.financeiro, 0))
  const funrural = centavos(grupos.reduce((s, g) => s + g.funrural, 0))
  // sem lote com valor, os % da ficha (ou do primeiro lote) só servem de rótulo
  const base = comValor[0]?.descontos ?? (f.lotes[0] ? descontosDoLote(f.lotes[0], f) : descontosDoLote({}, f))
  const cab = comAnimais.reduce((s, l) => s + l.cab, 0)
  const kgTotal = comAnimais.reduce((s, l) => s + l.kg, 0)
  const cabPesadas = comAnimais.reduce((s, l) => s + l.cabPesadas, 0)
  return {
    linhas,
    cab,
    kg: kgTotal,
    cabPesadas,
    // média do total só quando todos foram pesados: 2 bois pesados entre 26 cabeças não dão média de nada
    media: cabPesadas && cabPesadas === cab ? kgTotal / cabPesadas : null,
    arrobas: comAnimais.reduce((s, l) => s + (l.arrobas ?? 0), 0),
    temArroba: comAnimais.some((l) => l.lote.base === 'arroba'),
    bruto,
    temPreco,
    semValor: temPreco ? comAnimais.filter((l) => l.valor === null).map((l) => nomeDoLote(l.lote)) : [],
    financeiro,
    funrural,
    liquido: centavos(bruto - financeiro - funrural),
    pendentes: f.registros.filter((r) => r.pendente).length,
    grupos,
    descontosIguais,
    financeiroPct: descontosIguais ? base.financeiroPct : null,
    funruralPct: descontosIguais ? base.funruralPct : null,
  }
}

/**
 * Reparte um total em centavos inteiros pelos pesos, pelo maior resto. A soma
 * das partes é sempre o total. Pesos todos zero: tudo na primeira parte (só
 * acontece com total zero na prática, mas a soma tem de bater mesmo assim).
 * (Mora aqui desde a 3D para as contas por porteira e por lote usarem a mesma.)
 */
export function ratear(totalCentavos: number, pesos: number[]): number[] {
  const n = pesos.length
  if (!n) return []
  const soma = pesos.reduce((s, p) => s + Math.max(0, p), 0)
  if (!(soma > 0)) return pesos.map((_, i) => (i === 0 ? totalCentavos : 0))
  const sinal = totalCentavos < 0 ? -1 : 1
  const total = Math.abs(totalCentavos)
  const exatos = pesos.map((p) => (total * Math.max(0, p)) / soma)
  const partes = exatos.map((x) => Math.floor(x + 1e-9))
  let sobra = total - partes.reduce((s, x) => s + x, 0)
  // os centavos que sobram vão para as maiores frações; empate, para a primeira
  const ordem = exatos.map((x, i) => ({ i, resto: x - Math.floor(x + 1e-9) }))
    .sort((a, b) => b.resto - a.resto || a.i - b.i)
  for (let k = 0; sobra > 0 && k < ordem.length; k++, sobra--) partes[ordem[k]!.i]!++
  return partes.map((x) => x * sinal)
}

/**
 * «à vista 2% + Funrural 1,63%», «Funrural 1,63%», «sem desconto» — o resumo
 * dos descontos de um lote. R1C: «à vista» é o desconto financeiro; o
 * Funrural é o repasse do desconto do frigorífico (NOME_FUNRURAL, tipos.ts).
 */
export function textoDosDescontos(d: Descontos): string {
  const n = (v: number) => v.toLocaleString('pt-BR', { maximumFractionDigits: 2 })
  if (d.financeiroPct && d.funruralPct) return `à vista ${n(d.financeiroPct)}% + Funrural ${n(d.funruralPct)}%`
  if (d.financeiroPct) return `à vista ${n(d.financeiroPct)}%`
  if (d.funruralPct) return `Funrural ${n(d.funruralPct)}%`
  return 'sem desconto'
}

/** «Bezerro · R$ 1.900/cab.» — o nome que o lote tem na lista da pesagem e no romaneio. */
export function nomeDoLote(l: Lote): string {
  if (l.preco === null || !(l.preco > 0)) return l.categoria
  const p = l.preco.toLocaleString('pt-BR', { minimumFractionDigits: l.preco % 1 ? 2 : 0, maximumFractionDigits: 2 })
  const u = l.base === 'arroba' ? '/@' : l.base === 'kg' ? '/kg' : '/cab.'
  return `${l.categoria} · R$ ${p}${u}`
}

export interface SituacaoDePagamento {
  /** soma das quitações que valem (anuladas não contam) */
  pago: number
  aPagar: number
  /** vencimento mais próximo entre as parcelas ainda não cobertas */
  proximoVencimento: string | null
  quitado: boolean
  /** quanto de cada parcela já está coberto, na mesma ordem de `f.pagamentos` */
  parcelas: { id: string; coberto: number; quitada: boolean }[]
}

/**
 * Pago é o que o GESTOR quitou — nunca o que alguém anotou na parcela (etapa 1).
 * O dinheiro quitado cobre as parcelas pela ordem de vencimento, como no
 * banco: pagou 50 mil de 100 mil em duas de 50, a primeira está paga e a
 * segunda, aberta — seja qual for a parcela que o gestor marcou.
 */
export function situacaoDePagamento(
  f: Pick<Ficha, 'pagamentos'>,
  liquido: number,
  quitacoes: Pick<Quitacao, 'valor' | 'anuladaEm' | 'recusada'>[] = [],
): SituacaoDePagamento {
  // anulada ou recusada pelo servidor não é dinheiro que saiu
  const pago = centavos(quitacoes.filter((q) => !q.anuladaEm && !q.recusada).reduce((s, q) => s + q.valor, 0))
  const aPagar = centavos(Math.max(0, liquido - pago))
  const ordem = f.pagamentos.map((p, i) => ({ p, i }))
    .sort((a, b) => (a.p.vencimento || '9999').localeCompare(b.p.vencimento || '9999') || a.i - b.i)
  const parcelas: SituacaoDePagamento['parcelas'] = f.pagamentos.map((p) => ({ id: p.id, coberto: 0, quitada: false }))
  let sobra = pago
  for (const { p, i } of ordem) {
    const coberto = centavos(Math.min(p.valor, sobra))
    sobra = centavos(sobra - coberto)
    parcelas[i] = { id: p.id, coberto, quitada: coberto >= p.valor }
  }
  const quitado = liquido > 0 && aPagar === 0
  const proxima = quitado ? undefined : ordem.find(({ p, i }) => !parcelas[i]!.quitada && p.vencimento)
  return { pago, aPagar, proximoVencimento: proxima?.p.vencimento ?? null, quitado, parcelas }
}

/** Comissão da ficha = valor por cabeça × cabeças do romaneio. */
export function comissaoTotal(porCabeca: number, cab: number): number {
  return centavos(porCabeca * cab)
}

export interface CustoDeChegada {
  aoVendedor: number
  gastos: number
  total: number
  porCabeca: number | null
  porKg: number | null
  /** só quando todos os lotes são por arroba: misturar dá número sem sentido */
  porArroba: number | null
}

/**
 * Quanto o gado custou de fato no destino — relatório interno do comprador.
 * O que se pagou ao vendedor é o líquido do romaneio; os gastos são os lançados
 * depois (comissão, frete…), em valor total.
 */
export function custoDeChegada(f: Ficha, r: Resultado): CustoDeChegada {
  const gastos = somaDosGastos(f)
  const total = centavos(r.liquido + gastos)
  return { aoVendedor: r.liquido, gastos, total, ...porUnidade(total, r) }
}

function somaDosGastos(f: Pick<Ficha, 'custos'>): number {
  return centavos(gastosValendo(f).reduce((s, c) => s + c.valor, 0))
}

/**
 * R2B · Os gastos que valem: o anulado (com motivo) fica na ficha, riscado,
 * e sai de todas as somas — como a quitação anulada. Ficha antiga (sem a
 * lista) não quebra.
 */
export function gastosValendo<T extends Pick<Custo, 'anuladoEm'>>(f: { custos?: T[] | null }): T[] {
  return Array.isArray(f.custos) ? f.custos.filter((c) => c && !c.anuladoEm) : []
}

/**
 * R2B · O valor da comissão do comprador: por cabeça × as cabeças do dia em
 * que foi LANÇADA (o banco anota, 013) — é o que o acerto paga. Comissão só
 * deste aparelho (sem a anotação): as cabeças de hoje. Um número só em todas
 * as telas (Pagamento, Custo, Contas, Relatório, Extrato).
 */
export function valorDaComissao(c: Pick<Comissao, 'porCabeca' | 'cabecas'>, cabHoje: number): { cab: number; valor: number } {
  const cab = typeof c.cabecas === 'number' && Number.isFinite(c.cabecas) ? c.cabecas : cabHoje
  return { cab, valor: comissaoTotal(c.porCabeca, cab) }
}

/**
 * Um total dividido pelo gado: por cabeça sempre; por kg só com todos pesados;
 * por arroba só quando todos os lotes são por arroba (misturar dá número sem
 * sentido). Mesma regra para o custo de chegada e para o fechamento.
 */
function porUnidade(total: number, r: Resultado): Pick<CustoDeChegada, 'porCabeca' | 'porKg' | 'porArroba'> {
  const todosPesados = r.cab > 0 && r.cabPesadas === r.cab
  const soArroba = r.linhas.filter((l) => l.cab > 0).every((l) => l.lote.base === 'arroba')
  return {
    porCabeca: r.cab ? total / r.cab : null,
    porKg: todosPesados && r.kg ? total / r.kg : null,
    porArroba: soArroba && r.arrobas ? total / r.arrobas : null,
  }
}

/* ---------- 2E · fechamento da compra para o escritório ---------- */

export interface Fechamento {
  /**
   * o líquido do romaneio (bruto − financeiro − funrural) e quanto o gestor já
   * quitou. 3F: `aCargoDoCliente` é a parte das porteiras Cliente (intermediação),
   * que o cliente paga direto — fica fora do «falta pagar» do gestor.
   */
  vendedor: { valor: number; pago: number; aPagar: number; quitado: boolean; aCargoDoCliente: number }
  /** nulo: não lançada, ou quem olha não pode ver (comprador). R2B: `pago` = no acerto (tudo) ou fora dele (014) */
  comissao: { porCabeca: number; cab: number; valor: number; acertada: boolean; pago: number; falta: number } | null
  /** quem vê comissão e ela ainda não foi lançada: o total sai sem ela, e a tela avisa */
  comissaoPendente: boolean
  /** versão do comprador: nenhuma linha, valor ou total que deixe deduzir a comissão */
  semComissao: boolean
  /** os gastos que valem (o anulado fica de fora; a tela o mostra riscado) */
  gastos: Custo[]
  totalGastos: number
  total: number
  porCabeca: number | null
  porKg: number | null
  porArroba: number | null
  /**
   * R2B · A parte de cada gasto que é do GESTOR (`gastosDoGestor`, destinos.ts):
   * o gasto da porteira Cliente é zero — o cliente paga direto. Sem porteira
   * de cliente, é o gasto inteiro.
   */
  doGestor: Record<string, number>
  totalGastosDoGestor: number
  /** R2B · o custo do gestor: vendedor (a parte dele) + gastos dele + comissão do comprador */
  custoDoGestor: number
  /** R2B · conferência: a parte do cliente (vendedor das porteiras Cliente + os gastos delas) — não é dinheiro do gestor */
  custoDoCliente: number
  /** R2B · o que falta o gestor pagar: vendedor + gastos dele sem «pago em» + comissão em aberto */
  aPagar: number
}

/**
 * O que pagar e para quem: vendedor, comissão, frete e outros gastos — em
 * contas diferentes, por isso cada um numa linha. O custo total é o que o
 * gado custou de fato, ligado à compra, e se divide por cabeça e por arroba.
 *
 * `verComissao` é o papel de quem olha (gestor e Consulta sim, comprador não).
 * Sem ele, a comissão nem entra na conta, mesmo que alguém a passe — o total
 * do comprador é outro número, e não um total com uma linha escondida.
 */
export function fechamentoDaCompra(
  f: Pick<Ficha, 'custos' | 'pagamentos'>,
  r: Resultado,
  o: {
    verComissao: boolean
    comissao?: Pick<Comissao, 'porCabeca' | 'acertoId' | 'cabecas'> | null
    quitacoes?: Pick<Quitacao, 'valor' | 'anuladaEm' | 'recusada'>[]
    /** 3F · a parte do vendedor que os clientes pagam direto (`divisaoDoPagamento`, destinos.ts) */
    aCargoDoCliente?: number
    /** R2B · a parte de cada gasto que é do gestor (`gastosDoGestor`, destinos.ts); ausente: o gasto inteiro */
    gastosDoGestor?: Map<string, number>
    /** R2B · o que já se pagou da comissão do comprador fora do acerto (migração 014) */
    comissaoPagaFora?: number
  },
): Fechamento {
  const aCargoDoCliente = centavos(o.aCargoDoCliente ?? 0)
  const pg = situacaoDePagamento(f, centavos(r.liquido - aCargoDoCliente), o.quitacoes ?? [])
  const c = o.verComissao && o.comissao ? o.comissao : null
  const vc = c ? valorDaComissao(c, r.cab) : null
  const pagoComissao = c && vc ? (c.acertoId ? vc.valor : centavos(Math.min(vc.valor, o.comissaoPagaFora ?? 0))) : 0
  const comissao = c && vc ? { porCabeca: c.porCabeca, cab: vc.cab, valor: vc.valor, acertada: !!c.acertoId, pago: pagoComissao, falta: centavos(vc.valor - pagoComissao) } : null
  const gastos = gastosValendo(f).map((x) => ({ ...x }))
  const totalGastos = somaDosGastos(f)
  const total = centavos(r.liquido + (comissao?.valor ?? 0) + totalGastos)
  const doGestor: Record<string, number> = {}
  for (const g of gastos) doGestor[g.id] = centavos(o.gastosDoGestor?.get(g.id) ?? g.valor)
  const totalGastosDoGestor = centavos(gastos.reduce((s, g) => s + doGestor[g.id]!, 0))
  const gastosAPagar = centavos(gastos.reduce((s, g) => s + (g.pagoEm ? 0 : doGestor[g.id]!), 0))
  const comissaoFalta = comissao ? comissao.falta : 0
  return {
    vendedor: { valor: r.liquido, pago: pg.pago, aPagar: pg.aPagar, quitado: pg.quitado || (aCargoDoCliente > 0 && pg.aPagar === 0), aCargoDoCliente },
    comissao,
    comissaoPendente: o.verComissao && !o.comissao,
    semComissao: !o.verComissao,
    gastos,
    totalGastos,
    total,
    ...porUnidade(total, r),
    doGestor,
    totalGastosDoGestor,
    custoDoGestor: centavos(r.liquido - aCargoDoCliente + totalGastosDoGestor + (comissao?.valor ?? 0)),
    custoDoCliente: centavos(aCargoDoCliente + totalGastos - totalGastosDoGestor),
    aPagar: centavos((r.temPreco ? pg.aPagar : 0) + gastosAPagar + comissaoFalta),
  }
}

/**
 * «Custo pendente»: ficha encerrada cujo custo ninguém fechou — ou que mudou
 * depois de fechado (reabriu e pesou mais, corrigiu a negociação, lançou
 * gasto). Compara o total SEM comissão, que é o que o comprador também vê;
 * corrigir a comissão depois não reabre o custo.
 */
export function custoPendente(f: Pick<Ficha, 'situacao' | 'custos' | 'custoFechado'> & { finalidade?: Ficha['finalidade'] }, r: Resultado): boolean {
  if (f.situacao !== 'encerrada') return false
  // só a COMPRA tem custo a fechar para o escritório; na venda a aba mostra o
  // resultado (simulação de 06/10/2026: vendas apareciam «custo pendente»)
  if (f.finalidade && f.finalidade !== 'Compra') return false
  if (!f.custoFechado) return true
  return centavos(f.custoFechado.semComissao) !== totalSemComissao(f, r)
}

/** Vendedor + gastos da ficha: o número que se guarda ao fechar o custo. */
export function totalSemComissao(f: Pick<Ficha, 'custos'>, r: Resultado): number {
  return centavos(r.liquido + somaDosGastos(f))
}

/* ---------------------------------------------------------------------------
 * 3E · Versão nova da negociação em ficha encerrada — e o romaneio final
 * ------------------------------------------------------------------------- */

/**
 * O romaneio desta ficha ainda é parcial? Só enquanto a ficha está aberta
 * (pesando). Desde a 3E, ficha aberta não tem papel: mostra a prévia.
 */
export function romaneioParcial(f: Pick<Ficha, 'situacao'>): boolean {
  return f.situacao === 'aberta'
}

/**
 * Corrigir a negociação de uma ficha ENCERRADA (o «Salvar nova versão do
 * romaneio»): sobe a versão, guarda no histórico o que mudou e como era, e
 * troca lotes e forma. NÃO toca a situação nem os pesos — por isso o
 * romaneio que se abre em seguida é o FINAL da versão nova, nunca um
 * «parcial» (defeito relatado no test drive de 06/10/2026, conferido em
 * testes/contas.mjs).
 */
export function aplicarNovaVersao(f: Ficha, depois: Pick<Negociacao, 'lotes'>, forma: Forma, mudou: string[], em: string): void {
  f.historico.push({
    em, oque: `Versão ${f.versao + 1} — ${mudou.join('; ')}`,
    antes: { lotes: structuredClone(f.lotes), financeiroPct: f.financeiroPct, funruralPct: f.funruralPct },
  })
  f.versao++
  f.lotes = structuredClone(depois.lotes)
  f.forma = forma
}
